import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Home as HomeIcon, Plus, Bell, User, Library as LibraryIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Bottom navigation — 4 tabs + a UNIVERSAL center FAB.
 *
 * The center FAB always does exactly one thing: opens the Capture screen.
 * It's the app's single, unambiguous "upload / save something" button.
 * (Previously the FAB icon changed per route — users found that confusing.)
 */
const TABS = [
  { key: "home",      label: "HOME",      icon: HomeIcon,      to: "/app/chat" },
  { key: "reminders", label: "REMINDERS", icon: Bell,          to: "/app/reminders" },
  { key: "library",   label: "LIBRARY",   icon: LibraryIcon,   to: "/app/library" },
  { key: "profile",   label: "PROFILE",   icon: User,          to: "/app/profile" },
];

export default function BottomNav() {
  const nav = useNavigate();
  const location = useLocation();
  const [dueCount, setDueCount] = useState(0);

  useEffect(() => {
    const h = (e) => setDueCount(e.detail?.count || 0);
    window.addEventListener("impo:due-count", h);
    return () => window.removeEventListener("impo:due-count", h);
  }, []);

  const onFabTap = () => {
    nav("/app/capture");
  };

  const captureActive = location.pathname.startsWith("/app/capture");

  return (
    <div
      className="sticky bottom-0 left-0 right-0 z-40"
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0px)" }}
      data-testid="bottom-tab-bar"
    >
      <div
        className="relative h-[68px] grid grid-cols-5 items-center px-2"
        style={{
          background: "linear-gradient(180deg, #0b0d10 0%, #050608 100%)",
          color: "rgba(255,255,255,0.72)",
        }}
      >
        {TABS.slice(0, 2).map((t) => (
          <NavItem
            key={t.key}
            tab={t}
            prominent={t.key === "home"}
            badge={t.key === "reminders" && dueCount ? dueCount : 0}
          />
        ))}
        <div className="flex items-center justify-center">
          <button
            aria-label="Add — save anything"
            onClick={onFabTap}
            className={cn(
              "relative -mt-6 h-14 w-14 rounded-full grid place-items-center transition active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40",
              captureActive && "ring-2 ring-primary/60"
            )}
            style={{
              background: captureActive
                ? "radial-gradient(circle at 30% 30%, hsl(var(--primary)), hsl(var(--primary) / 0.75))"
                : "radial-gradient(circle at 30% 30%, #2a2d33, #0a0b0d)",
              boxShadow: "0 10px 24px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.10)",
            }}
            data-testid="bottom-tab-add-fab"
            data-fab-action="Capture"
          >
            <Plus className="h-6 w-6 text-white" strokeWidth={2.2} />
            <span
              className="pointer-events-none absolute inset-0 rounded-full"
              style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.05)" }}
            />
          </button>
        </div>
        {TABS.slice(2).map((t) => (
          <NavItem key={t.key} tab={t} />
        ))}
      </div>
    </div>
  );
}

function NavItem({ tab, prominent = false, badge = 0 }) {
  const { icon: Icon, label, to, key } = tab;
  return (
    <NavLink
      to={to}
      end={to === "/app/chat"}
      className={({ isActive }) =>
        cn(
          "h-[68px] flex flex-col items-center justify-center gap-1 text-white transition",
          isActive ? "opacity-100" : "opacity-60"
        )
      }
      data-testid={`bottom-tab-${key}-button`}
    >
      {({ isActive }) => (
        <>
          <div className="relative">
            <div
              className={cn(
                "h-9 w-9 rounded-full flex items-center justify-center transition",
                isActive && prominent && "bg-primary text-primary-foreground",
                isActive && !prominent && "bg-white/10"
              )}
            >
              <Icon className={cn("h-5 w-5", isActive ? "stroke-[2.2]" : "stroke-[1.9]")} />
            </div>
            {badge > 0 && (
              <span
                className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-semibold grid place-items-center"
                data-testid={`nav-badge-${key}`}
              >
                {badge > 9 ? "9+" : badge}
              </span>
            )}
          </div>
          <span className={cn("text-[10px] tracking-wider mt-1", prominent && "font-semibold text-[9px]")}>{label}</span>
        </>
      )}
    </NavLink>
  );
}
